import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const AREA_KEYS = ['health', 'career', 'money', 'relationships', 'mind', 'creative'] as const

const LIFESTYLE_KEYS = [
  'busyProfessional',
  'student',
  'parent',
  'buildingSomething',
  'betweenThings',
  'remoteWorker',
] as const

interface LifeCoachFlowProps {
  onBack: () => void
  onGoalSelected: (goalText: string) => void
  getSuggestions: (
    lifeArea: string,
    lifestyle: string,
    freeText: string,
    opts?: { name?: string; timeAvailable?: string },
  ) => Promise<string[] | null>
  isLoading: boolean
  // Inside onboarding: a sub-step label instead of "Step N of 3", and the
  // fill fraction is reported for the onboarding bar.
  inOnboarding?: boolean
  onProgress?: (fraction: number) => void
}

type FlowStep = 'area' | 'lifestyle' | 'wish' | 'suggestions'

function ChipButton({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-medium transition-all ${
        selected
          ? 'border-primary/70 bg-primary/10 text-primary-text'
          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}

export default function LifeCoachFlow({
  onBack,
  onGoalSelected,
  getSuggestions,
  isLoading,
  inOnboarding,
  onProgress,
}: LifeCoachFlowProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<FlowStep>('area')
  const [area, setArea] = useState('')
  const [lifestyle, setLifestyle] = useState('')
  const [wishText, setWishText] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])

  async function handleWishSubmit(text = wishText) {
    const results = await getSuggestions(
      t(`planner.coach.areas.${area}`),
      t(`planner.coach.lifestyles.${lifestyle}`),
      text,
    )
    if (results) {
      setSuggestions(results)
      setStep('suggestions')
    }
  }

  const stepLabels: Record<FlowStep, string> = {
    area: t('planner.coach.stepArea'),
    lifestyle: t('planner.coach.stepLifestyle'),
    wish: t('planner.coach.stepWish'),
    suggestions: t('planner.coach.pickTitle'),
  }

  const stepOrder: FlowStep[] = ['area', 'lifestyle', 'wish', 'suggestions']
  const currentIndex = stepOrder.indexOf(step)
  const totalSteps = 3

  useEffect(() => {
    onProgress?.(Math.min(currentIndex + 1, totalSteps) / totalSteps)
  }, [currentIndex])

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={step === 'area' ? onBack : () => setStep(stepOrder[currentIndex - 1] ?? 'area')}
          className="cursor-pointer rounded-md text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          ← {t('planner.coach.back')}
        </button>
        {step !== 'suggestions' && (
          <span className="text-xs text-muted-foreground/60">
            {inOnboarding
              ? t('planner.coach.subStep', { current: currentIndex + 1, total: totalSteps })
              : t('planner.step', { current: currentIndex + 1, total: totalSteps })}
          </span>
        )}
      </div>

      {/* Step label */}
      <div>
        <h2 className="text-xl font-bold">{stepLabels[step]}</h2>
        {step === 'suggestions' && (
          <p className="mt-1 text-sm text-muted-foreground">{t('planner.coach.pickSubtitle')}</p>
        )}
      </div>

      {/* Step: life area */}
      {step === 'area' && (
        <div className="flex flex-wrap gap-2">
          {AREA_KEYS.map((key) => (
            <ChipButton
              key={key}
              label={t(`planner.coach.areas.${key}`)}
              selected={area === key}
              onClick={() => {
                setArea(key)
                setStep('lifestyle')
              }}
            />
          ))}
        </div>
      )}

      {/* Step: lifestyle */}
      {step === 'lifestyle' && (
        <div className="flex flex-wrap gap-2">
          {LIFESTYLE_KEYS.map((key) => (
            <ChipButton
              key={key}
              label={t(`planner.coach.lifestyles.${key}`)}
              selected={lifestyle === key}
              onClick={() => {
                setLifestyle(key)
                setStep('wish')
              }}
            />
          ))}
        </div>
      )}

      {/* Step: free text wish */}
      {step === 'wish' && (
        <div className="space-y-3">
          <textarea
            value={wishText}
            onChange={(e) => setWishText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && wishText.trim()) {
                e.preventDefault()
                handleWishSubmit()
              }
            }}
            placeholder={t('planner.coach.wishPlaceholder')}
            rows={3}
            autoFocus
            disabled={isLoading}
            className="w-full resize-none rounded-2xl border border-border/60 bg-card/40 px-5 py-4 text-base leading-relaxed backdrop-blur-sm placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          />
          <button
            type="button"
            onClick={() => handleWishSubmit()}
            disabled={!wishText.trim() || isLoading}
            className="cursor-pointer flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:scale-100"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('planner.coach.generating')}
              </>
            ) : (
              t('planner.coach.next')
            )}
          </button>
          <button
            type="button"
            onClick={() => handleWishSubmit('')}
            disabled={isLoading}
            className="mx-auto block cursor-pointer rounded-md text-xs text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('planner.coach.skipWish')}
          </button>
        </div>
      )}

      {/* Step: suggestions */}
      {step === 'suggestions' && (
        <div className="space-y-3">
          {suggestions.map((goal, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                onGoalSelected(goal)
                setTimeout(onBack, 180)
              }}
              className="cursor-pointer w-full rounded-2xl border border-border/60 bg-card/40 px-5 py-4 text-left text-sm leading-relaxed text-foreground backdrop-blur-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:scale-[1.01] active:scale-[0.99]"
            >
              {goal}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
